'use client';

import React from 'react';
import { Modal } from '@/app/components/Modal';
import { BaseButton } from '@/app/components/buttons/BaseButton';
import type { UnsavedChangesModalOptions } from './types';
import AlarmIcon from '/public/icons/alarm.svg';

interface UnsavedChangesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  options?: UnsavedChangesModalOptions;
}

export const UnsavedChangesModal: React.FC<UnsavedChangesModalProps> = ({ isOpen, onClose, onSave, options }) => {
  const { title, description, cancelButtonText, discardButtonText, onCancel, onDiscard } = options || {};

  const defaultTitle = 'You have unsaved changes';
  const defaultDescription = 'Leaving now will discard any updates. Do you want to save your changes before leaving?';
  const defaultCancelButtonText = 'Cancel';
  const defaultDiscardButtonText = 'Discard Changes';

  const handleCancel = () => {
    if (onCancel) {
      onCancel({ onCancel: onClose, onSave });
    } else {
      onClose();
    }
  };

  const handleDiscard = () => {
    if (onDiscard) {
      onDiscard({ onCancel: onClose, onSave });
    } else {
      onSave();
    }
  };

  return (
    <Modal open={isOpen} onClose={onClose}>
      <div className="px-9 py-11.5 flex flex-col items-center text-center max-w-113.5 max-md:px-4 max-md:pt-6 max-md:pb-4">
        <AlarmIcon className="text-status-warning fill-current mb-5" />
        <h2 className="light text-blue-900 mb-1.5">{title || defaultTitle}</h2>
        <p className="text-body-regular text-grey-700 max-md:text-sm max-md:leading-[150%]">
          {description || defaultDescription}
        </p>
        <div className="flex gap-5 w-full mt-9 max-md:mt-6 max-md:gap-4">
          <BaseButton
            disabled={options?.isSecondaryActionLoading}
            intent="secondary"
            onClick={handleCancel}
            size="full"
            className="max-md:h-auto max-md:min-h-10.5 max-md:min-w-0 max-md:flex-1 max-md:whitespace-normal max-md:px-2 max-md:text-sm"
          >
            {cancelButtonText || defaultCancelButtonText}
          </BaseButton>
          <BaseButton
            disabled={options?.isPrimaryActionLoading}
            intent="primary"
            onClick={handleDiscard}
            size="full"
            className="max-md:h-auto max-md:min-h-10.5 max-md:min-w-0 max-md:flex-1 max-md:whitespace-normal max-md:px-2 max-md:text-sm"
          >
            {discardButtonText || defaultDiscardButtonText}
          </BaseButton>
        </div>
      </div>
    </Modal>
  );
};
